import { useEffect, useState } from "react";
import { MapPin, Truck, Crosshair, Spinner, CaretDown } from "@phosphor-icons/react";
import { MX_STATES, loadLocation, saveLocation, detectByIp, detectByGps, quoteShipping, fetchShippingConfig } from "@/lib/shipping";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);

// Tarjeta "Envío a tu ubicación": detecta por IP, permite GPS o elegir estado/municipio y cotiza.
// `productIds` → productos a cotizar (se cobra el más alto). `onQuote(q, loc)` notifica al padre.
export default function ShippingEstimator({ productIds, subtotalMxn, onQuote, compact = false }) {
  const [loc, setLoc] = useState(() => loadLocation());
  const [quote, setQuote] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ state: "", city: "" });
  const [err, setErr] = useState("");

  useEffect(() => { fetchShippingConfig().then(setCfg).catch(() => {}); }, []);
  useEffect(() => {
    if (loc) return;
    let alive = true;
    detectByIp().then((l) => { if (alive && l) { setLoc(l); saveLocation(l); } }).catch(() => {});
    return () => { alive = false; };
  }, [loc]);
  useEffect(() => {
    if (!productIds?.length) return;
    let alive = true;
    quoteShipping(productIds, loc).then((q) => { if (alive) { setQuote(q); onQuote?.(q, loc); } }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds.join(","), loc?.state, loc?.city]);

  const apply = (l) => { setLoc(l); saveLocation(l); setEditing(false); setErr(""); };
  const useGps = async () => {
    setBusy(true); setErr("");
    try { apply(await detectByGps()); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const isBC = draft.state === "Baja California";
  const canApply = !!draft.state && (!isBC || !!draft.city);
  const place = loc ? [loc.city, loc.state].filter(Boolean).join(", ") : null;

  return (
    <div className={`rounded-2xl border ${quote?.free ? "border-emerald-200 bg-emerald-50/60" : "border-zinc-200 bg-white"} ${compact ? "p-3" : "p-4 sm:p-5"}`} data-testid="shipping-estimator">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${quote?.free ? "bg-emerald-600 text-white" : "bg-zinc-900 text-white"}`}><Truck size={18} weight="bold" /></div>
        <div className="flex-1 min-w-0">
          {!loc ? (
            <div className="text-sm text-zinc-700" data-testid="shipping-no-location">Calcula tu envío: <button type="button" onClick={() => setEditing(true)} className="font-semibold underline underline-offset-2">elige tu ubicación</button> o <button type="button" onClick={useGps} className="font-semibold underline underline-offset-2">usa tu GPS</button>.</div>
          ) : quote ? (
            <>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1"><MapPin size={11} weight="fill" /> Envío a <span className="text-zinc-800 font-semibold normal-case tracking-normal" data-testid="shipping-place">{place}</span></div>
              <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
                {quote.free ? (
                  <span className="font-heading text-lg font-extrabold text-emerald-700" data-testid="shipping-cost">¡Envío GRATIS!</span>
                ) : (
                  <span className="font-heading text-lg font-extrabold text-zinc-900" data-testid="shipping-cost">+ {fmt(quote.cost_mxn)} de envío</span>
                )}
                <span className="text-[11px] text-zinc-500" data-testid="shipping-zone">{quote.zone_label}</span>
              </div>
              {!quote.free && subtotalMxn > 0 && (
                <div className="text-xs text-zinc-600 mt-0.5">Total con envío: <b className="text-zinc-900 numeric" data-testid="shipping-total">{fmt(subtotalMxn + quote.cost_mxn)}</b></div>
              )}
              {quote.free && cfg && <div className="text-[11px] text-emerald-800/80 mt-0.5">Entrega local gratis en {cfg.local_municipalities.join(", ")}.</div>}
              {!quote.free && cfg && <div className="text-[11px] text-zinc-500 mt-0.5" data-testid="shipping-local-hint">Gratis solo en {cfg.local_municipalities.join(", ")}.</div>}
            </>
          ) : <div className="text-sm text-zinc-500 inline-flex items-center gap-1"><Spinner size={13} className="animate-spin" /> Calculando envío…</div>}
          <div className="mt-2 flex items-center gap-3 text-[11px]">
            <button type="button" onClick={() => { setDraft({ state: loc?.state || "", city: "" }); setEditing((v) => !v); }} className="inline-flex items-center gap-1 font-semibold text-[#002FA7] hover:underline" data-testid="shipping-change">Cambiar ubicación <CaretDown size={10} weight="bold" /></button>
            <button type="button" onClick={useGps} disabled={busy} className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-900 disabled:opacity-50" data-testid="shipping-gps">{busy ? <Spinner size={11} className="animate-spin" /> : <Crosshair size={11} weight="bold" />} Usar mi ubicación exacta</button>
          </div>
          {err && <div className="text-[11px] text-[#E0000F] mt-1" data-testid="shipping-error">{err}</div>}
          {editing && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2" data-testid="shipping-picker">
              <select value={draft.state} onChange={(e) => setDraft({ state: e.target.value, city: "" })} className="h-9 text-sm border border-zinc-300 rounded-lg px-2 bg-white" data-testid="shipping-state">
                <option value="">Estado…</option>
                {MX_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {isBC ? (
                <select value={draft.city} onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))} className="h-9 text-sm border border-zinc-300 rounded-lg px-2 bg-white" data-testid="shipping-city">
                  <option value="">Municipio…</option>
                  {(cfg?.bc_municipalities || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : (
                <input value={draft.city} onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))} placeholder="Ciudad (opcional)" className="h-9 text-sm border border-zinc-300 rounded-lg px-2 bg-white" data-testid="shipping-city" />
              )}
              <button type="button" disabled={!canApply} onClick={() => apply({ state: draft.state, city: draft.city.trim(), source: "manual" })} className="h-9 px-4 rounded-lg bg-zinc-900 text-white text-xs font-semibold disabled:opacity-40" data-testid="shipping-apply">Calcular</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
